import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Store } from 'lucide-react'

import { createWriterScriptListing } from '@/api/studio-writer'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoModal, ProtoTextarea } from '@/features/studio/components/ui-kit'

export function WriterScriptListingModal({
  open,
  onClose,
  scriptId,
  episodeNo,
  lockedRevision,
  defaultTitle,
}: {
  open: boolean
  onClose: () => void
  scriptId: number
  episodeNo: number
  lockedRevision: number
  defaultTitle: string
}) {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [price, setPrice] = useState('800')
  const [summary, setSummary] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setTitle(defaultTitle)
    setPrice('800')
    setSummary('')
    setError(null)
  }, [open, defaultTitle, episodeNo, lockedRevision])

  async function handleSubmit() {
    const parsed = Number(price)
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError(t('script.listingPriceInvalid'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await createWriterScriptListing(scriptId, episodeNo, {
        title: title.trim() || defaultTitle,
        summary: summary.trim(),
        price_points: Math.floor(parsed),
        license_mode: 'non_exclusive',
      })
      const sellUrl = `/marketplace?tab=sell&listing=${encodeURIComponent(res.listing_public_id)}`
      window.location.assign(sellUrl)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'script.listingFailed'))
      setSubmitting(false)
    }
  }

  return (
    <ProtoModal
      open={open}
      onClose={onClose}
      title={t('script.listingTitle')}
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('script.listingCancel')}
          </ProtoBtn>
          <ProtoBtn size="sm" disabled={submitting} onClick={() => void handleSubmit()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Store className="h-4 w-4" />}
            {t('script.listingConfirm')}
          </ProtoBtn>
        </>
      }
    >
      {error ? <p className="mb-3 text-sm text-rose-300">{error}</p> : null}
      <p className="mb-4 text-xs text-zinc-500">{t('writer.listing.hint', { rev: lockedRevision })}</p>
      <div className="space-y-3">
        <div>
          <ProtoLabel>{t('script.listingName')}</ProtoLabel>
          <ProtoInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <ProtoLabel>{t('script.listingPrice')}</ProtoLabel>
          <ProtoInput type="number" min={1} value={price} onChange={(e) => setPrice(e.target.value)} />
        </div>
        <div>
          <ProtoLabel>{t('script.listingSummary')}</ProtoLabel>
          <ProtoTextarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3} />
        </div>
      </div>
    </ProtoModal>
  )
}
